// src/components/Plot.tsx
// Observable Plot を React にラップ。
// `spec` は Plot.plot() に渡す PlotOptions。再描画は spec の identity で判定。
//
// responsiveWidth: container の幅を ResizeObserver で測って Plot.plot に渡す。
//   options.width は SVG の最小幅 (= container が指定以上なら responsive 上書き、
//   指定未満なら options.width をそのまま使う … は しない。 単純に container 幅で上書き)。
//   options.aspectRatio が指定されてれば height も比例縮小、 そうでなければ height は固定。
import { useEffect, useRef, useState } from "react";
import * as Plot from "@observablehq/plot";

export interface PlotProps {
  options: Plot.PlotOptions;
  className?: string;
  /**
   * onCellClick: Plot.cell でレンダリングされた要素を click したときに発火。
   *   cell の data (__data__) を引数で渡す。
   *   v2.1: HeatMap drilldown 用に追加
   */
  onCellClick?: (datum: unknown) => void;
  /**
   * true なら container 幅を ResizeObserver で測って Plot に渡す。
   * options.width で指定された値を上限として、 container 幅に合わせて縮小する。
   * height は options.height (or 比例) のまま。
   * iPad / モバイル対応 (2026-05-23 追加)。
   */
  responsiveWidth?: boolean;
}

export function PlotFigure({ options, className, onCellClick, responsiveWidth }: PlotProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);

  // container 幅を測る (responsiveWidth=true のとき only)
  useEffect(() => {
    if (!responsiveWidth) return;
    const c = containerRef.current;
    if (!c) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        // Math.floor で 1px サブピクセル揺らぎを吸収、 30px 未満は無視 (初回 0px 等)
        const w = Math.floor(e.contentRect.width);
        if (w >= 30) setMeasuredWidth(w);
      }
    });
    ro.observe(c);
    return () => ro.disconnect();
  }, [responsiveWidth]);

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    // responsiveWidth 有効 + 測定済み + container 幅が options.width より小さければ上書き
    const baseWidth = typeof options.width === "number" ? options.width : null;
    const effectiveWidth = (responsiveWidth && measuredWidth != null && baseWidth != null)
      ? Math.min(baseWidth, measuredWidth)
      : null;
    const effectiveOpts = effectiveWidth != null
      ? { ...options, width: effectiveWidth }
      : options;
    const node = Plot.plot(effectiveOpts);
    c.replaceChildren(node);

    // Cell click 対応: Plot.cell=rect, Plot.dot=circle, Plot.barX=rect
    if (onCellClick) {
      const sels = "rect[aria-label], circle[aria-label]";
      const cells = node.querySelectorAll(sels);
      cells.forEach((el) => {
        (el as SVGElement).style.cursor = "pointer";
        el.addEventListener("click", (e) => {
          const datum = (e.currentTarget as unknown as { __data__: unknown }).__data__;
          if (datum != null) onCellClick(datum);
        });
      });
    }

    return () => {
      if (c.firstChild) c.removeChild(c.firstChild);
    };
  }, [options, onCellClick, responsiveWidth, measuredWidth]);

  return <div ref={containerRef} className={className} />;
}
